<template>
  <base-loader
    :class="$style.container"
    :loading="loading"
    :preloaded="preloaded"
    @loading="$emit('loading')"
    @loaded="$emit('loaded')"
  >
    <template #loader>
      <content-loader />
    </template>
    <template #default>
      <div
        data-qa="popup-layout"
        :class="$style.layout"
      >
        <slot
          name="heading"
          :title="title"
        >
          <popup-heading v-if="hasTitle">
            <render-slot
              :slot-content="titleSlot"
              data-qa="title"
            >
              {{ title }}
            </render-slot>
          </popup-heading>
        </slot>

        <slot name="body">
          <popup-body
            v-if="hasBody"
            :single-column="singleColumn"
            :padding-disabled="paddingDisabled"
            :class="$style.body"
          >
            <template #prepend>
              <render-slot :slot-content="prependSlot" />
            </template>
            <template #main>
              <render-slot :slot-content="mainSlot" />
            </template>
            <template #side>
              <render-slot :slot-content="sideSlot" />
            </template>
            <template #append>
              <render-slot :slot-content="appendSlot" />
            </template>
          </popup-body>
        </slot>
        <div :class="{ [$style.stickyFooter]: stickyFooter }">
          <popup-footer v-if="showFooter && hasFooter">
            <slot name="footer-content" />
            <error-wrapper>
              <slot name="error" />
            </error-wrapper>
            <render-slot :slot-content="footerLayoutSlot">
              <ui-stack gap="s">
                <render-slot :slot-content="footerSlot" />
              </ui-stack>
            </render-slot>
          </popup-footer>
        </div>
      </div>
    </template>
  </base-loader>
</template>

<script>
import { computed } from 'vue';
import { UiStack } from '@huntflow/ui';
import BaseLoader from '@/shared/legacy/ui/loader/base-loader.vue';
import ContentLoader from '@/shared/legacy/ui/loader/content-loader.vue';
import RenderSlot from '@/shared/legacy/ui/render-slot/render-slot.vue';
import { SlotChecker } from '@/shared/legacy/lib/util/slot-checker';
import PopupBody from '@/shared/legacy/ui/popup-layout/body.vue';
import ErrorWrapper from '@/shared/legacy/ui/popup-layout/error-wrapper.vue';
import PopupFooter from '@/shared/legacy/ui/popup-layout/footer.vue';
import PopupHeading from '@/shared/legacy/ui/popup-layout/heading.vue';

export default {
  name: 'PopupLayout',
  components: {
    BaseLoader,
    ContentLoader,
    RenderSlot,
    ErrorWrapper,
    PopupHeading,
    PopupBody,
    PopupFooter,
    UiStack
  },
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    preloaded: {
      type: Boolean,
      default: undefined
    },
    singleColumn: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    showFooter: {
      type: Boolean,
      default: true
    },
    stickyFooter: {
      type: Boolean,
      default: true
    },
    paddingDisabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['loading', 'loaded'],
  setup(props) {
    const titleSlot = SlotChecker.useSlotContent('title', { title: props.title });

    const hasTitle = computed(() => Boolean(props.title) || Boolean(titleSlot.value));

    const footerSlot = SlotChecker.useSlotContent('footer');
    const footerLayoutSlot = SlotChecker.useSlotContent('footer_layout');

    const prependSlot = SlotChecker.useSlotContent('prepend');
    const mainSlot = SlotChecker.useSlotContent('main');
    const sideSlot = SlotChecker.useSlotContent('side');
    const appendSlot = SlotChecker.useSlotContent('append');

    const hasFooter = computed(() => Boolean(footerSlot.value) || Boolean(footerLayoutSlot.value));
    const hasBody = computed(
      () =>
        Boolean(prependSlot.value)
        || Boolean(mainSlot.value)
        || Boolean(sideSlot.value)
        || Boolean(appendSlot.value)
    );

    return {
      hasTitle,
      titleSlot,
      footerSlot,
      footerLayoutSlot,
      hasFooter,
      prependSlot,
      mainSlot,
      sideSlot,
      appendSlot,
      hasBody
    };
  }
};
</script>

<style module lang="less">
@import '~@less/common/variables.less';

.container {
  --content-loader-height: var(--popup-layout-min-height, 150px);
}
.layout {
  min-height: var(--popup-layout-min-height, auto);
  display: flex;
  flex-direction: column;
}
.body {
  flex: 1 0 0;
}
.stickyFooter {
  position: sticky;
  bottom: 0;
  z-index: @zindex-modal;
}
</style>

<i18n lang="json">{}</i18n>
